'use client'

import { FC, useEffect, useLayoutEffect, useRef } from 'react'
import { useCustomRouter, useGetTranslations, useSetAuthToken } from '@/root/shared-hooks'
import { Banner, Button } from '@/root/ui/tuuleliinid'
import { MobiilIdIcon } from '@/root/ui/tuuleliinid/icons/MobiilIdIcon'
import type { AuthVariant } from '../../constants'
import { useMobileIdFlow } from './useMobileIdFlow'

interface MobileIdAuthProps {
  variant: AuthVariant
  onLoadingChange?: (isLoading: boolean) => void
  onSuccess?: (accessToken: string, personalIdentificationNumber: string) => void
}

export const MobileIdAuth: FC<MobileIdAuthProps> = ({ variant, onLoadingChange, onSuccess }) => {
  const { getTranslation } = useGetTranslations()
  const { saveToken } = useSetAuthToken()
  const { push, locale } = useCustomRouter()

  const pushRef = useRef(push)
  const saveTokenRef = useRef(saveToken)
  const localeRef = useRef(locale)
  const onSuccessRef = useRef(onSuccess)
  const pendingPinRef = useRef('')

  useLayoutEffect(() => {
    pushRef.current = push
  }, [push])

  useLayoutEffect(() => {
    saveTokenRef.current = saveToken
  }, [saveToken])

  useLayoutEffect(() => {
    localeRef.current = locale
  }, [locale])

  useLayoutEffect(() => {
    onSuccessRef.current = onSuccess
  }, [onSuccess])

  const isSignup = variant === 'signup'
  const { errorMessage, handleSubmit: handleFlowSubmit, isLoading, isPreparing, isProcessing, isWaitingForConfirmation, verificationCode } =
    useMobileIdFlow({
      locale,
      onSuccess: async (accessToken) => {
        if (onSuccessRef.current) {
          onSuccessRef.current(accessToken, pendingPinRef.current)
          return
        }
        await saveTokenRef.current(accessToken)
        await pushRef.current(`/${localeRef.current}/my/details`)
      },
      messages: {
        invalidRequest: getTranslation('authMobileIdInvalidRequest'),
        prepareError: getTranslation('authMobileIdPrepareError'),
        processError: getTranslation('authMobileIdProcessError'),
      },
    })

  useEffect(() => {
    onLoadingChange?.(isLoading)
  }, [isLoading, onLoadingChange])

  const buttonText = isPreparing
    ? getTranslation('authMobileIdPreparing')
    : isProcessing
      ? getTranslation('authMobileIdProcessing')
      : isSignup
        ? getTranslation('authMobileIdSignup')
        : getTranslation('authMobileIdLogin')

  if (isWaitingForConfirmation && verificationCode) {
    return (
      <div className="flex w-full flex-col items-center gap-4">
        <MobiilIdIcon width={24} height={24} />
        <p className="text-primary-base text-lg font-bold">{getTranslation('authMobileIdTitle')}</p>
        <p className="text-base-normal text-center text-sm">{getTranslation('authMobileIdWaiting')}</p>
        <p className="text-base-normal text-xs">{getTranslation('authMobileIdVerificationCode')}</p>
        <p className="text-primary-base text-4xl font-bold tracking-widest">{verificationCode}</p>
        <p className="text-base-normal max-w-xs text-center text-xs">{getTranslation('authMobileIdCodeHint')}</p>
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col items-center">
      <MobiilIdIcon width={24} height={24} />

      <p className="text-primary-base mt-2 text-lg font-bold">{getTranslation('authMobileIdTitle')}</p>

      <p className="text-base-normal mt-2 text-center text-sm">{getTranslation('authMobileIdDescription')}</p>

      {errorMessage ? <Banner variant="error" align="left" body={errorMessage} className="mt-4 mb-2 w-full" /> : null}

      <form onSubmit={(e) => { pendingPinRef.current = String(new FormData(e.currentTarget).get('personalIdentificationNumber') ?? ''); handleFlowSubmit(e) }} className="mt-6 flex w-full flex-col gap-4">
        <div className="flex flex-col gap-1">
          <label htmlFor="mobileid-pid" className="text-base-normal text-sm">
            {getTranslation('authMobileIdPersonalIdLabel')}
          </label>
          <input
            id="mobileid-pid"
            name="personalIdentificationNumber"
            type="text"
            inputMode="numeric"
            pattern="\d{11}"
            maxLength={11}
            placeholder={getTranslation('authMobileIdPersonalIdPlaceholder')}
            disabled={isLoading}
            className="border-base-medium-light focus:border-primary-base rounded-lg border px-3 py-2 text-sm transition-colors outline-none disabled:opacity-50"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="mobileid-phone" className="text-base-normal text-sm">
            {getTranslation('authMobileIdPhoneLabel')}
          </label>
          <input
            id="mobileid-phone"
            name="phoneNumber"
            type="tel"
            inputMode="tel"
            placeholder={getTranslation('authMobileIdPhonePlaceholder')}
            disabled={isLoading}
            className="border-base-medium-light focus:border-primary-base rounded-lg border px-3 py-2 text-sm transition-colors outline-none disabled:opacity-50"
          />
        </div>

        <Button
          type="submit"
          variant="contained"
          disabled={isLoading}
          className="flex h-12 w-full items-center justify-center rounded-2xl text-lg font-bold whitespace-nowrap disabled:hover:brightness-100"
        >
          {buttonText}
        </Button>
      </form>
    </div>
  )
}
